import { RUNNING_VARIANTS } from "../runningTraining";
import { compileTrainingStructure, STRUCTURED_TRAINING_TEMPLATES } from "../trainingStructure";
import { hasTrainingStructure, trainingDisplayItem } from "../trainingCompatibility";

const number = (value, min, max) => {
  if (!["number", "string"].includes(typeof value) || String(value).trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= min && n <= max ? n : null;
};
const minutes = (n) => Math.round(n * 60000);
const zonedInstant = (value) => typeof value === "string" && /(?:Z|[+-]\d{2}:\d{2})$/i.test(value) && Number.isFinite(Date.parse(value));

// Only saved structured fields are executable. Titles, descriptions and presets never fill gaps.
export function buildRunningPlan(item) {
  const invalid = (reason) => ({ ok: false, reason, plan: null });
  const structured = hasTrainingStructure(item);
  item = trainingDisplayItem(item);
  if (structured) {
    if (!item?.training_structure) return invalid("incompletePlan");
    if (item.training_structure.version === 2) {
      if (!["training", "workout", "recovery"].includes(item.category || item.kind || "training")) return invalid("unsupported");
      return compileTrainingStructure(item.training_structure);
    }
    if ((item.training_unit_type || item.unit_type) !== "individual_training"
      || !STRUCTURED_TRAINING_TEMPLATES.includes(item.training_template)) return invalid("unsupported");
    return compileTrainingStructure(item.training_structure);
  }
  if (!item || (item.training_unit_type || item.unit_type) !== "running_training" || !RUNNING_VARIANTS.includes(item.running_variant)) return invalid("unsupported");
  const variant = item.running_variant;
  const sections = [];
  const add = (kind, durationMs, extra = {}) => sections.push(Object.freeze({ id: `section-${sections.length + 1}`, kind, durationMs, ...extra }));
  let repetitions = 0;
  if (["recovery_run", "endurance_run", "tempo_run"].includes(variant)) {
    let duration = null;
    if (zonedInstant(item.start_at) && zonedInstant(item.end_at)) {
      duration = number((Date.parse(item.end_at) - Date.parse(item.start_at)) / 60000, 0.1, 1440);
    }
    if (duration === null) duration = number(item.duration_minutes ?? item.duration, 0.1, 1440);
    if (duration === null) return invalid("missingDuration");
    add("run", minutes(duration));
  } else {
    const d = item.running_details;
    if (!d || typeof d !== "object" || Array.isArray(d)) return invalid("incompletePlan");
    const reps = number(d.repetitions, 1, 100);
    const warmup = number(d.warmup_minutes, 0, 120);
    const sprint = variant === "sprint_acceleration";
    const work = number(sprint ? d.distance_m : d.work_minutes, sprint ? 1 : 0.1, sprint ? 1000 : 120);
    const rest = reps === 1 ? 0 : number(sprint ? d.rest_seconds : d.rest_minutes, 0, sprint ? 600 : 120);
    const cooldown = sprint ? 0 : number(d.cooldown_minutes, 0, 120);
    if (!Number.isInteger(reps) || [warmup, work, rest, cooldown].some(n => n === null)) return invalid("incompletePlan");
    repetitions = reps;
    if (warmup > 0) add("warmup", minutes(warmup));
    for (let rep = 1; rep <= reps; rep += 1) {
      add(sprint ? "sprint" : "work", sprint ? null : minutes(work), { repetition: rep, repetitions: reps, ...(sprint ? { distanceM: work } : {}) });
      if (rep < reps && rest > 0) add("rest", sprint ? Math.round(rest * 1000) : minutes(rest), { repetition: rep, repetitions: reps });
    }
    if (cooldown > 0) add("cooldown", minutes(cooldown));
  }
  const knownDurationMs = sections.reduce((sum, s) => sum + (s.durationMs ?? 0), 0);
  if (knownDurationMs > 86400000) return invalid("incompletePlan");
  const plan = Object.freeze({ version: 1, variant, repetitions, sections: Object.freeze(sections), knownDurationMs,
    totalDurationMs: sections.some(s => s.durationMs === null) ? null : knownDurationMs });
  return { ok: true, reason: null, plan };
}
